import { apiRequest, apiText } from "@/lib/api";
import type { components } from "@/lib/api-types";

/**
 * The Curative client.
 *
 * Separate from `lib/api.ts` so the module's surface stays inside `web/src/curative/` (C0.1, D-37) —
 * `lib/api.ts` is v1's client and G5 watches it. The request helper is reused rather than reimplemented,
 * because auth, error shape and the base URL are not things to have two versions of.
 */
type S = components["schemas"];

export type CvBoard = S["CvBoard"];
export type CvBoardDetail = S["CvBoardDetail"];
export type CvBoardPage = S["CvBoardPage"];
export type CvAttachPasteResult = S["CvAttachPasteResult"];
export type CvAttachPhonesResult = S["CvAttachPhonesResult"];
export type CvBoardCreate = S["CvBoardCreate"];
export type CvBoardUpdate = S["CvBoardUpdate"];
export type CvBoardField = S["CvBoardField"];
export type CvCardConfig = S["CvCardConfig"];
export type CvDealRow = S["CvDealRow"];
export type CvDealCreate = S["CvDealCreate"];
export type CvDealUpdate = S["CvDealUpdate"];
export type CvStage = S["CvStage"];
export type CvStageUpsert = S["CvStageUpsert"];
export type CvView = S["CvView"];
export type CvViewUpsert = S["CvViewUpsert"];
export type CvQuickAction = NonNullable<CvCardConfig["quick_actions"]>[number];
export type CvDealDetail = S["CvDealDetail"];
// v2.8: the curative intake SOP (D-110).
export type CvEnrichment = S["CvEnrichment"];
export type CvRuleAction = S["CvRuleAction"];
export type CvIntakeRules = S["CvIntakeRules"];
// v2.11: the BRIEF, the dial order, the cadence, the call outcome, Activity and Today (D-141 … D-144).
export type CvBrief = S["CvBrief"];
// v3.0: the Lane B call companion (D-148 …).
export type CvDialView = S["CvDialView"];
export type CvDialCandidate = S["CvDialCandidate"];
export type CvDialOutcomeIn = S["CvDialOutcomeIn"];
export type CvUnassignedNotes = S["CvUnassignedNotes"];
export type CvCallNoteBody = S["CvCallNoteBody"];
export type CvCallNoteStatus = S["CvCallNoteStatus"];
export type CvDialOutcomeResult = S["CvDialOutcomeResult"];
export type CvHeirResolution = S["CvHeirResolution"];
export type CvDialPhone = S["CvDialPhone"];
export type CvCadenceState = S["CvCadenceState"];
export type CvCadenceTemplate = S["CvCadenceTemplate"];
export type CvCallOutcomeCreate = S["CvCallOutcomeCreate"];
export type CvCallOutcomeResult = S["CvCallOutcomeResult"];
export type CvActivityItem = S["CvActivityItem"];
export type CvToday = S["CvToday"];
export type CvTodayRow = S["CvTodayRow"];
export type CvNote = S["CvNote"];
export type CvNoteCreate = S["CvNoteCreate"];
export type CvNoteUpdate = S["CvNoteUpdate"];
export type CvReminder = S["CvReminder"];
export type CvReminderCreate = S["CvReminderCreate"];
export type CvReminderUpdate = S["CvReminderUpdate"];
export type CvDocument = S["CvDocument"];
export type CvEvent = S["CvEvent"];
export type CvTag = S["CvTag"];
export type CvDealMove = S["CvDealMove"];
export type CvField = S["CvField"];
export type CvFieldCreate = S["CvFieldCreate"];
export type CvImportPreview = S["CvImportPreview"];
export type CvImportResult = S["CvImportResult"];
export type CvImportCapabilities = S["CvImportCapabilities"];
export type CvSmartList = S["CvSmartList"];
export type CvSmartListPage = S["CvSmartListPage"];
export type CvSmartListRow = S["CvSmartListRow"];
export type CvSmartListUpsert = S["CvSmartListUpsert"];
export type CvDashboard = S["CvDashboard"];
export type CvDashboardPrefs = S["CvDashboardPrefs"];
export type CvTile = S["CvTile"];
export type CvFunnelStage = S["CvFunnelStage"];
export type CvDashReminder = S["CvReminderRow"];
export type CvUser = S["CvUser"];
export type CvTree = S["CvTree"];
export type CvTreeCreate = S["CvTreeCreate"];
export type CvPerson = S["CvPerson"];
export type CvPersonCreate = S["CvPersonCreate"];
export type CvPersonUpdate = S["CvPersonUpdate"];
export type CvPersonEvidence = S["CvPersonEvidence"];
export type CvEvidenceType = CvPersonEvidence["doc_type"];
export type CvLiving = CvPerson["living_status"];
export type CvCaseRole = CvPerson["case_role"];
export type CvMatter = S["CvMatter"];
export type CvMatterCreate = S["CvMatterCreate"];
export type CvMatterStage = CvMatter["stage"];
export type CvMatterUpdateCreate = S["CvMatterUpdateCreate"];
export type CvCashPanel = S["CvCashPanel"];
export type CvCashCreate = S["CvCashCreate"];
export type CvCashType = CvCashCreate["type"];
export type CvAssessment = S["CvAssessment"];
export type CvAssessmentSave = S["CvAssessmentSave"];
export type CvPatternScore = S["CvPatternScore"];
export type CvScorecardTemplate = S["CvScorecardTemplate"];
export type CvReviewRow = S["CvReviewRow"];
export type CvDecision = S["CvDecision"];
export type CvAgentRun = S["CvAgentRun"];
export type CvUnderwritePanel = S["CvUnderwritePanel"];
export type CvAiTooling = S["CvAiTooling"];
export type CvInboxView = S["CvInboxView"];
export type CvConversation = S["CvConversation"];
export type CvConversationDetail = S["CvConversationDetail"];
export type CvTouch = S["CvTouch"];
export type CvTouchCreate = S["CvTouchCreate"];
export type CvChannel = CvTouch["channel"];
export type CvCallOutcome = NonNullable<CvTouch["outcome"]>;
export type CvFollowUp = S["CvFollowUp"];
export type CvSettings = S["CvSettings"];
export type CvSettingsUpdate = S["CvSettingsUpdate"];
export type CvTagRename = S["CvTagRename"];
// ---- v2.1
export type CvFieldRole = CvField["role"];
export type CvFieldEdit = S["CvFieldEdit"];
export type CvPreset = S["CvPreset"];
export type CvAppearance = S["CvAppearance"];
export type CvPalette = CvAppearance["palette"];
export type CvThemeMode = CvAppearance["mode"];
export type CvTileLibrary = S["CvTileLibrary"];
export type CvDashSectionOut = S["CvDashSectionOut"];
export type CvDashTile = S["CvDashTile"];
export type CvDashLayout = S["CvDashLayout"];
export type CvDashSection = CvDashTile["section"];
export type CvCustomTile = S["CvCustomTile"];
export type CvCustomTileUpsert = S["CvCustomTileUpsert"];
export type CvSpend = S["CvSpend"];
export type CvSpendUpsert = S["CvSpendUpsert"];
export type CvSpendChannel = CvSpend["channel"];

const BASE = "/api/v1/curative";

/** The board toolbar, as a query (C1.2). Every one of these lives in the URL, so refresh and Back work. */
export interface CvBoardQuery {
  q?: string;
  stage_id?: string[];
  owner_id?: string[];
  tag_id?: string[];
  tag_mode?: "any" | "all";
  exclude_tag_id?: string[];
  since?: string;
  open_reminders?: boolean;
  include_archived?: boolean;
  sort?: string;
  order?: "asc" | "desc";
  limit?: number;
}

function stringify(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  throw new TypeError(`query parameter is not a scalar: ${typeof value}`);
}

function qs(query: CvBoardQuery | Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query as Record<string, unknown>)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    // Every value here is a scalar or an array of scalars by construction; `stringify` keeps that
    // true rather than letting an object reach the query string as "[object Object]".
    if (Array.isArray(v)) v.forEach((item) => p.append(k, stringify(item)));
    else p.set(k, stringify(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const cv = {
  boards: (archived = false) =>
    apiRequest<CvBoard[]>(`${BASE}/boards${archived ? "?archived=true" : ""}`),
  board: (id: string) => apiRequest<CvBoardDetail>(`${BASE}/boards/${id}`),
  createBoard: (body: CvBoardCreate) =>
    apiRequest<CvBoardDetail>(`${BASE}/boards`, { method: "POST", body: JSON.stringify(body) }),
  updateBoard: (id: string, body: CvBoardUpdate) =>
    apiRequest<CvBoardDetail>(`${BASE}/boards/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  putStages: (id: string, stages: CvStageUpsert[]) =>
    apiRequest<CvStage[]>(`${BASE}/boards/${id}/stages`, {
      method: "PUT",
      body: JSON.stringify({ stages }),
    }),
  putBoardFields: (
    id: string,
    fields: { field_id: string; required: boolean; show_on_card: boolean }[],
  ) =>
    apiRequest<CvBoardField[]>(`${BASE}/boards/${id}/fields`, {
      method: "PUT",
      body: JSON.stringify({ fields }),
    }),
  putCardConfig: (id: string, body: CvCardConfig) =>
    apiRequest<CvCardConfig>(`${BASE}/boards/${id}/card-config`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  boardDeals: (id: string, query: CvBoardQuery = {}) =>
    apiRequest<CvBoardPage>(`${BASE}/boards/${id}/deals${qs(query)}`),

  /**
   * Download the filtered board as CSV.
   *
   * Fetched and turned into a blob rather than `window.location.href = url`: the API authenticates
   * with a bearer token, and a top-level navigation cannot carry a header — so the href version
   * answers 401 and the browser downloads nothing. The C1 gate caught exactly that.
   */
  exportBoard: async (id: string, name: string, query: CvBoardQuery = {}): Promise<void> => {
    const csv = await apiText(`${BASE}/boards/${id}/export${qs(query)}`);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `${name.toLowerCase().replace(/\s+/g, "-")}-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(href);
  },

  createDeal: (boardId: string, body: CvDealCreate) =>
    apiRequest<CvDealRow>(`${BASE}/boards/${boardId}/deals`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deal: (id: string) => apiRequest<CvDealRow>(`${BASE}/deals/${id}`),
  updateDeal: (id: string, body: CvDealUpdate) =>
    apiRequest<CvDealRow>(`${BASE}/deals/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  // ---- deal detail (C2). One query serves both the page and the board drawer, and every mutation
  // answers with the whole detail again, so the Timeline on screen is never a step behind the edit.
  dealDetail: (id: string) => apiRequest<CvDealDetail>(`${BASE}/deals/${id}/detail`),
  // v2.2 P2/P3.2
  updateNote: (noteId: string, body: CvNoteUpdate) =>
    apiRequest<CvNote>(`${BASE}/notes/${noteId}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteNote: (noteId: string) => apiRequest<void>(`${BASE}/notes/${noteId}`, { method: "DELETE" }),
  // v2.7 P3 (D-107/D-108): phones onto a deal, and the paste-to-attach preview/apply on a board.
  attachDealPhones: (dealId: string, body: { phones: string[]; source: "suggestion" | "paste" | "manual" }) =>
    apiRequest<CvAttachPhonesResult>(`${BASE}/deals/${dealId}/attach-phones`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  attachPhones: (boardId: string, body: { text: string; apply: boolean }) =>
    apiRequest<CvAttachPasteResult>(`${BASE}/boards/${boardId}/attach-phones`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  setHot: (dealId: string, hot: boolean) =>
    apiRequest<CvDealDetail>(`${BASE}/deals/${dealId}/hot`, {
      method: "POST",
      body: JSON.stringify({ hot }),
    }),
  addNote: (id: string, body: CvNoteCreate) =>
    apiRequest<CvDealDetail>(`${BASE}/deals/${id}/notes`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  addReminder: (id: string, body: CvReminderCreate) =>
    apiRequest<CvDealDetail>(`${BASE}/deals/${id}/reminders`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateReminder: (reminderId: string, body: CvReminderUpdate) =>
    apiRequest<CvDealDetail>(`${BASE}/reminders/${reminderId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  moveDeal: (id: string, body: CvDealMove) =>
    apiRequest<CvDealDetail>(`${BASE}/deals/${id}/move`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteDeal: (id: string) => apiRequest<void>(`${BASE}/deals/${id}`, { method: "DELETE" }),
  // v2.9 P3.1: a column's own sort; an empty key returns it to the board's.
  setStageSort: (stageId: string, body: { key: string | null; order: "asc" | "desc" }) =>
    apiRequest<CvStage[]>(`${BASE}/stages/${stageId}/sort`, { method: "PUT", body: JSON.stringify(body) }),

  tags: () => apiRequest<CvTag[]>(`${BASE}/tags`),
  upsertTag: (body: { name: string; color?: string | null }) =>
    apiRequest<CvTag>(`${BASE}/tags`, { method: "POST", body: JSON.stringify(body) }),

  /**
   * Upload one document: signed URL, PUT the bytes straight to Storage, then record the row.
   *
   * The row is written only after the PUT succeeds, so a failed upload leaves no document behind
   * claiming to exist — the opposite order is how a deal ends up listing a file that 404s.
   */
  uploadDocument: async (dealId: string, file: File): Promise<CvDealDetail> => {
    const signed = await apiRequest<{
      storage_path: string;
      upload_url: string;
      token: string;
      expires_in: number;
    }>(`${BASE}/deals/${dealId}/documents/upload-url`, {
      method: "POST",
      body: JSON.stringify({ filename: file.name }),
    });

    const put = await fetch(signed.upload_url, {
      method: "PUT",
      headers: {
        ...(signed.token ? { Authorization: `Bearer ${signed.token}` } : {}),
        "Content-Type": file.type || "application/octet-stream",
      },
      body: file,
    });
    if (!put.ok) throw new Error(`upload failed: ${put.status} ${put.statusText}`);

    return apiRequest<CvDealDetail>(`${BASE}/deals/${dealId}/documents`, {
      method: "POST",
      body: JSON.stringify({
        storage_path: signed.storage_path,
        name: file.name,
        mime: file.type || null,
        size_bytes: file.size,
      }),
    });
  },

  // ---- import (C3)
  importCapabilities: () => apiRequest<CvImportCapabilities>(`${BASE}/import/capabilities`),
  importTables: (file: File) => {
    const form = new FormData();
    form.append("file", file);
    return apiRequest<string[]>(`${BASE}/import/tables`, { method: "POST", body: form });
  },
  importPreview: (file: File, table?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (table) form.append("table", table);
    return apiRequest<CvImportPreview>(`${BASE}/import/preview`, { method: "POST", body: form });
  },
  runImport: (
    boardId: string,
    file: File,
    body: {
      mapping: Record<string, string | null>;
      stage_id: string;
      title_template?: string;
      table?: string;
    },
  ) => {
    const form = new FormData();
    form.append("file", file);
    // Only the mapped columns travel: a header the operator left unmapped is one they decided not to
    // import, and sending it as null would have the server guess again.
    const mapped = Object.fromEntries(
      Object.entries(body.mapping).filter(([, key]) => Boolean(key)),
    );
    form.append("mapping", JSON.stringify(mapped));
    form.append("stage_id", body.stage_id);
    if (body.title_template) form.append("title_template", body.title_template);
    if (body.table) form.append("table", body.table);
    return apiRequest<CvImportResult>(`${BASE}/boards/${boardId}/import`, {
      method: "POST",
      body: form,
    });
  },

  fields: () => apiRequest<CvField[]>(`${BASE}/fields`),
  createField: (
    body: Omit<CvFieldCreate, "role"> & { role?: CvFieldRole },
    boardId?: string,
  ) =>
    apiRequest<CvField>(`${BASE}/fields${boardId ? `?board_id=${boardId}` : ""}`, {
      method: "POST",
      body: JSON.stringify({ role: "none", ...body }),
    }),
  allFields: () => apiRequest<CvField[]>(`${BASE}/fields?include_archived=true`),
  editField: (fieldId: string, body: CvFieldEdit) =>
    apiRequest<CvField>(`${BASE}/fields/${fieldId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  restoreField: (fieldId: string) =>
    apiRequest<CvField>(`${BASE}/fields/${fieldId}/restore`, { method: "POST" }),
  orderFields: (folder: string | null, fieldIds: string[]) =>
    apiRequest<CvField[]>(`${BASE}/fields-order`, {
      method: "PUT",
      body: JSON.stringify({ folder, field_ids: fieldIds }),
    }),
  presets: () => apiRequest<CvPreset[]>(`${BASE}/presets`),
  savePreset: (key: string, fieldKeys: string[]) =>
    apiRequest<CvPreset>(`${BASE}/presets/${key}`, {
      method: "PUT",
      body: JSON.stringify({ field_keys: fieldKeys }),
    }),

  // ---- the v2.1 tile library (T3)
  library: (period: "7d" | "30d" | "90d") =>
    apiRequest<CvDashboard>(`${BASE}/dashboard?period=${period}`),
  saveLayout: (body: CvDashLayout | Record<string, never>) =>
    apiRequest<CvDashLayout>(`${BASE}/dashboard/layout`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  createCustomTile: (body: CvCustomTileUpsert) =>
    apiRequest<CvCustomTile>(`${BASE}/dashboard/custom-tiles`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteCustomTile: (id: string) =>
    apiRequest<void>(`${BASE}/dashboard/custom-tiles/${id}`, { method: "DELETE" }),
  spend: () => apiRequest<CvSpend[]>(`${BASE}/spend`),
  logSpend: (body: CvSpendUpsert) =>
    apiRequest<CvSpend>(`${BASE}/spend`, { method: "POST", body: JSON.stringify(body) }),
  editSpend: (id: string, body: CvSpendUpsert) =>
    apiRequest<CvSpend>(`${BASE}/spend/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteSpend: (id: string) => apiRequest<void>(`${BASE}/spend/${id}`, { method: "DELETE" }),

  // ---- smart lists and the dashboard (C4)
  lists: () => apiRequest<CvSmartList[]>(`${BASE}/lists`),
  listRows: (
    listId: string,
    query: { q?: string; board_id?: string; sort?: string; order?: "asc" | "desc" } = {},
  ) => apiRequest<CvSmartListPage>(`${BASE}/lists/${listId}${qs(query)}`),
  createList: (body: CvSmartListUpsert) =>
    apiRequest<CvSmartList>(`${BASE}/lists`, { method: "POST", body: JSON.stringify(body) }),
  updateList: (listId: string, body: CvSmartListUpsert) =>
    apiRequest<CvSmartList>(`${BASE}/lists/${listId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteList: (listId: string) =>
    apiRequest<void>(`${BASE}/lists/${listId}`, { method: "DELETE" }),

  dashboard: (periodDays: number) =>
    apiRequest<CvDashboard>(`${BASE}/dashboard?period_days=${periodDays}`),
  saveDashboardPrefs: (body: CvDashboardPrefs) =>
    apiRequest<CvDashboardPrefs>(`${BASE}/dashboard/prefs`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  users: () => apiRequest<CvUser[]>(`${BASE}/users`),
  reassignReminder: (reminderId: string, assigneeUserId: string) =>
    apiRequest<CvDashReminder[]>(`${BASE}/reminders/${reminderId}/assignee`, {
      method: "POST",
      body: JSON.stringify({ assignee_user_id: assigneeUserId }),
    }),

  // ---- family tree, legal matters and cash (C5)
  tree: (dealId: string) => apiRequest<CvTree | null>(`${BASE}/deals/${dealId}/tree`),
  createTree: (dealId: string, body: CvTreeCreate) =>
    apiRequest<CvTree>(`${BASE}/deals/${dealId}/tree`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  addPerson: (treeId: string, body: CvPersonCreate) =>
    apiRequest<CvTree>(`${BASE}/trees/${treeId}/people`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updatePerson: (personId: string, body: CvPersonUpdate) =>
    apiRequest<CvTree>(`${BASE}/people/${personId}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  archivePerson: (personId: string) =>
    apiRequest<CvTree>(`${BASE}/people/${personId}`, { method: "DELETE" }),
  mergePeople: (keepId: string, mergeId: string) =>
    apiRequest<CvTree>(`${BASE}/trees/merge`, {
      method: "POST",
      body: JSON.stringify({ keep_id: keepId, merge_id: mergeId }),
    }),
  linkEvidence: (personId: string, documentId: string, docType: CvEvidenceType) =>
    apiRequest<CvTree>(`${BASE}/people/${personId}/evidence`, {
      method: "POST",
      body: JSON.stringify({ document_id: documentId, doc_type: docType }),
    }),
  unlinkEvidence: (personId: string, documentId: string) =>
    apiRequest<CvTree>(`${BASE}/people/${personId}/evidence/${documentId}`, { method: "DELETE" }),

  matters: (dealId: string) => apiRequest<CvMatter[]>(`${BASE}/deals/${dealId}/matters`),
  createMatter: (dealId: string, body: CvMatterCreate) =>
    apiRequest<CvMatter[]>(`${BASE}/deals/${dealId}/matters`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateMatter: (matterId: string, body: CvMatterCreate) =>
    apiRequest<CvMatter[]>(`${BASE}/matters/${matterId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  archiveMatter: (matterId: string) =>
    apiRequest<CvMatter[]>(`${BASE}/matters/${matterId}`, { method: "DELETE" }),
  addMatterUpdate: (matterId: string, body: CvMatterUpdateCreate) =>
    apiRequest<CvMatter[]>(`${BASE}/matters/${matterId}/updates`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  cash: (dealId: string) => apiRequest<CvCashPanel>(`${BASE}/deals/${dealId}/cash`),
  addCash: (dealId: string, body: CvCashCreate) =>
    apiRequest<CvCashPanel>(`${BASE}/deals/${dealId}/cash`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteCash: (eventId: string) =>
    apiRequest<CvCashPanel>(`${BASE}/cash/${eventId}`, { method: "DELETE" }),

  // ---- scorecard, review queue and the two AI jobs (C6)
  scorecardTemplate: () => apiRequest<CvScorecardTemplate>(`${BASE}/scorecard-template`),
  assessment: (dealId: string) =>
    apiRequest<CvAssessment | null>(`${BASE}/deals/${dealId}/assessment`),
  saveAssessment: (dealId: string, body: CvAssessmentSave) =>
    apiRequest<CvAssessment>(`${BASE}/deals/${dealId}/assessment`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  submitAssessment: (dealId: string) =>
    apiRequest<CvAssessment>(`${BASE}/deals/${dealId}/assessment/submit`, { method: "POST" }),
  reviewQueue: () => apiRequest<CvReviewRow[]>(`${BASE}/review`),
  decide: (assessmentId: string, body: CvDecision) =>
    apiRequest<CvAssessment>(`${BASE}/assessments/${assessmentId}/decision`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  aiTooling: () => apiRequest<CvAiTooling>(`${BASE}/ai/tooling`),
  underwriting: (dealId: string) =>
    apiRequest<CvUnderwritePanel>(`${BASE}/deals/${dealId}/underwriting`),
  runUnderwrite: (dealId: string) =>
    apiRequest<CvUnderwritePanel>(`${BASE}/deals/${dealId}/underwrite`, { method: "POST" }),
  setManualHeat: (dealId: string, heat: number, note: string) =>
    apiRequest<CvUnderwritePanel>(`${BASE}/deals/${dealId}/heat`, {
      method: "PUT",
      body: JSON.stringify({ heat, note }),
    }),
  clearManualHeat: (dealId: string) =>
    apiRequest<CvUnderwritePanel>(`${BASE}/deals/${dealId}/heat`, { method: "DELETE" }),
  docAnalyses: (dealId: string) =>
    apiRequest<CvAgentRun[]>(`${BASE}/deals/${dealId}/doc-analysis`),
  runDocAnalysis: (dealId: string, documentIds: string[]) =>
    apiRequest<CvAgentRun[]>(`${BASE}/deals/${dealId}/doc-analysis`, {
      method: "POST",
      body: JSON.stringify({ document_ids: documentIds }),
    }),
  applyDocUpdates: (
    dealId: string,
    body: { run_id: string; updates: { field_key: string; proposed: string }[]; save_to_notes: boolean },
  ) =>
    apiRequest<CvDealDetail>(`${BASE}/deals/${dealId}/doc-analysis/apply`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ---- the sales inbox and settings (C7). There is no send here, and no endpoint that could be one.
  inboxViews: () => apiRequest<CvInboxView[]>(`${BASE}/inbox/views`),
  inbox: (view: string, q?: string) =>
    apiRequest<CvConversation[]>(`${BASE}/inbox${qs({ view, q })}`),
  conversation: (id: string) =>
    apiRequest<CvConversationDetail>(`${BASE}/conversations/${id}`),
  logTouch: (dealId: string, body: CvTouchCreate) =>
    apiRequest<CvConversationDetail>(`${BASE}/deals/${dealId}/touches`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  setRead: (id: string, unread: boolean) =>
    apiRequest<CvConversationDetail>(`${BASE}/conversations/${id}/read?unread=${unread}`, {
      method: "POST",
    }),
  assignConversation: (id: string, assigneeUserId: string | null) =>
    apiRequest<CvConversationDetail>(`${BASE}/conversations/${id}/assignee`, {
      method: "POST",
      body: JSON.stringify({ assignee_user_id: assigneeUserId }),
    }),
  resolveConversation: (id: string, resolved: boolean) =>
    apiRequest<CvConversationDetail>(`${BASE}/conversations/${id}/resolve?resolved=${resolved}`, {
      method: "POST",
    }),
  conversationFollowUp: (id: string, body: CvFollowUp) =>
    apiRequest<CvConversationDetail>(`${BASE}/conversations/${id}/follow-up`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  settings: () => apiRequest<CvSettings>(`${BASE}/settings`),
  saveSettings: (body: CvSettingsUpdate) =>
    apiRequest<CvSettings>(`${BASE}/settings`, { method: "PUT", body: JSON.stringify(body) }),
  renameTag: (tagId: string, body: CvTagRename) =>
    apiRequest<CvTag[]>(`${BASE}/tags/${tagId}`, { method: "PUT", body: JSON.stringify(body) }),
  archiveTag: (tagId: string) =>
    apiRequest<CvTag[]>(`${BASE}/tags/${tagId}/archive`, { method: "POST" }),
  deleteTag: (tagId: string) =>
    apiRequest<CvTag[]>(`${BASE}/tags/${tagId}`, { method: "DELETE" }),
  archiveField: (fieldId: string) =>
    apiRequest<void>(`${BASE}/fields/${fieldId}/archive`, { method: "POST" }),

  views: (boardId: string) => apiRequest<CvView[]>(`${BASE}/boards/${boardId}/views`),
  saveView: (boardId: string, body: CvViewUpsert) =>
    apiRequest<CvView>(`${BASE}/boards/${boardId}/views`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateView: (viewId: string, body: CvViewUpsert) =>
    apiRequest<CvView>(`${BASE}/views/${viewId}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteView: (viewId: string) =>
    apiRequest<void>(`${BASE}/views/${viewId}`, { method: "DELETE" }),

  // v2.8 §6: the Enrichment panel, "Re-run enrichment", and R3/R4 on /system.
  enrichment: (dealId: string) => apiRequest<CvEnrichment>(`${BASE}/deals/${dealId}/enrichment`),
  rerunEnrichment: (dealId: string) =>
    apiRequest<CvEnrichment>(`${BASE}/deals/${dealId}/enrichment`, {
      method: "POST",
      body: JSON.stringify({ force: true }),
    }),
  intakeRules: () => apiRequest<CvIntakeRules>(`${BASE}/intake-rules`),
  setIntakeLive: (liveNow: boolean) =>
    apiRequest<CvIntakeRules>(`${BASE}/intake-rules`, {
      method: "PUT",
      body: JSON.stringify({ live_now: liveNow }),
    }),
  ruleActions: (includeDryRun = false) =>
    apiRequest<CvRuleAction[]>(
      `${BASE}/rule-actions${includeDryRun ? "?include_dry_run=true" : ""}`,
    ),
  undoRuleAction: (id: string) =>
    apiRequest<Record<string, string>>(`${BASE}/rule-actions/${id}/undo`, { method: "POST" }),

  // v2.11: Today, the BRIEF, the dial order, the call outcome and the cadence.
  today: () => apiRequest<CvToday>(`${BASE}/today`),
  brief: (dealId: string) => apiRequest<CvBrief>(`${BASE}/deals/${dealId}/brief`),
  rewriteBrief: (dealId: string) =>
    apiRequest<CvBrief>(`${BASE}/deals/${dealId}/brief`, { method: "POST" }),
  markPhone: (dealId: string, phone: string, action: "confirmed" | "wrong" | "dead" | "unconfirm") =>
    apiRequest<CvDialPhone[]>(`${BASE}/deals/${dealId}/phones/mark`, {
      method: "POST",
      body: JSON.stringify({ phone_e164: phone, action }),
    }),
  callOutcome: (dealId: string, body: CvCallOutcomeCreate) =>
    apiRequest<CvCallOutcomeResult>(`${BASE}/deals/${dealId}/call-outcome`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  startCadence: (dealId: string, restart = false) =>
    apiRequest<CvCadenceState>(`${BASE}/deals/${dealId}/cadence/start`, {
      method: "POST",
      body: JSON.stringify({ restart }),
    }),
  stopCadence: (dealId: string) =>
    apiRequest<CvCadenceState>(`${BASE}/deals/${dealId}/cadence/stop`, { method: "POST" }),
  boardCadence: (boardId: string) =>
    apiRequest<CvCadenceTemplate>(`${BASE}/boards/${boardId}/cadence`),
  saveBoardCadence: (boardId: string, body: { template: string; texts: Record<string, string> }) =>
    apiRequest<CvCadenceTemplate>(`${BASE}/boards/${boardId}/cadence`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // v3.0 §3: the Dial tab. Reads in one request; one tap logs the call (never a note).
  dial: (dealId: string) => apiRequest<CvDialView>(`${BASE}/deals/${dealId}/dial`),
  // v3.7 R2/R3: the tap (nothing dials) and the "notice given" toggle.
  dialIntent: (dealId: string, phone_e164: string) =>
    apiRequest<S["CvCallLogEntry"]>(`${BASE}/deals/${dealId}/dial/intent`, {
      method: "POST",
      body: JSON.stringify({ phone_e164 }),
    }),
  dialNotice: (dealId: string, phone_e164: string, given: boolean) =>
    apiRequest<S["CvCallLogEntry"]>(`${BASE}/deals/${dealId}/dial/notice`, {
      method: "POST",
      body: JSON.stringify({ phone_e164, given }),
    }),
  // v3.7 R3: Wispr's notes.
  unassignedNotes: () => apiRequest<CvUnassignedNotes>(`${BASE}/call-notes/unassigned`),
  assignNote: (noteId: string, touchId: string) =>
    apiRequest<CvCallNoteStatus>(`${BASE}/call-notes/${noteId}/assign`, {
      method: "POST",
      body: JSON.stringify({ touch_id: touchId }),
    }),
  dialOutcome: (dealId: string, body: CvDialOutcomeIn) =>
    apiRequest<CvDialOutcomeResult>(`${BASE}/deals/${dealId}/dial/outcome`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  // v3.3 §1: Sam's Confirm on a shadow candidate — the only path that makes it a card phone value.
  dialConfirmCandidate: (dealId: string, candidateId: string) =>
    apiRequest<CvDialView>(`${BASE}/deals/${dealId}/dial/candidates/${candidateId}/confirm`, {
      method: "POST",
    }),
  dialConfirm: (dealId: string, itemId: string, answer: string | null) =>
    apiRequest<CvHeirResolution>(`${BASE}/deals/${dealId}/dial/confirm`, {
      method: "POST",
      body: JSON.stringify({ item_id: itemId, answer }),
    }),
};
